import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { useReferrals } from '../Hooks/Api/useReferrals';
import UserStore from '../store/user-store';
import styles from './ReferralsPage.module.css';
import Lottie from 'react-lottie';
import checkmark from '../components/Lottie/checkmark.json';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import axios from "axios";
import {apiClient} from "../AxiosConfig";

const ReferralsPage = observer(() => {
    const { referrals, totalProfit, referralData, progressData, isLoading, error, refetch } = useReferrals();
    const [showCopyNotification, setShowCopyNotification] = useState(false);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showEarningsChart, setShowEarningsChart] = useState(false);
    const [showProgressSection, setShowProgressSection] = useState(true);
    const [selectedCurrency, setSelectedCurrency] = useState('usd'); // 'usd' or 'rub'
    const [showWithdrawalSection, setShowWithdrawalSection] = useState(false);
    const [shareMsgId, setShareMsgId] = useState(null);
    const navigate = useNavigate();
    
    useEffect(() => {
        document.body.style.overflowY = 'scroll';
        
        // Запрос к API для получения msg_id
        const prepareReferral = async () => {
            try {
                const response = await apiClient.post('v1/referrals/prepare-referral', {ref_code: UserStore.ref_code});
                
                if (response.status === 200) {
                    const data = response.data
                    if (data?.msg_id) {
                        setShareMsgId(data.msg_id);
                        // Сохраняем в стор
                        UserStore.setShareMsgId(data.msg_id);
                    }
                }
            } catch (err) {
                console.error('Failed to prepare referral:', err);
            }
        };
        
        prepareReferral();
        
        return () => {
            document.body.style.overflow = 'hidden';
        };
    }, []);

    const TG_WEB_APP = window.Telegram?.WebApp;
    const refCode = UserStore.ref_code;
    const appUrl = process.env.REACT_APP_MINI_URL;
    const referralLink = refCode ? `${appUrl}?startapp=ref_code=${refCode}` : '';

    const translations = {
        en: {
            title: 'Invite Friends',
            subtitle: 'Earn rewards for every friend who joins',
            referralLinkTitle: 'Your Referral Link',
            copyButton: 'Copy',
            copiedButton: 'Copied!',
            shareButton: 'Share',
            shareStoryButton: 'Share to Story',
            totalProfitTitle: 'Total Earnings',
            referralsListTitle: 'Your Referrals',
            usd: 'USD',
            rub: 'RUB',
            emptyState: 'No referrals yet',
            emptyStateSubtitle: 'Share your link to start earning',
            loading: 'Loading...',
            modalTitle: 'Referral Details',
            registeredAt: 'Registered',
            converted: 'Converted',
            notConverted: 'Not Converted',
            profit: 'Profit',
            userPlan: 'Plan',
            userStatus: 'Status',
            telegramId: 'Telegram ID',
            languageCode: 'Language',
            close: 'Close',
            referralProgressTitle: 'Referral Progress',
            referralsCount: 'Total Referrals',
            currentPercentage: 'Current Commission',
            progressDescription: 'Track your referral progress and reach milestones to earn more rewards.',
            milestone5: '0-5 refs',
            milestone10: '10+ refs',
            milestone6: '5-9 refs',
            upgradeTitle: 'Upgrade Your Earnings!',
            upgradeSubtitle: 'Get a subscription to increase your referral commission',
            upgradeDescription: 'With a subscription, your referral tiers will be:',
            upgradeTiers: '5% → 9% → 15%',
            upgradeButton: 'Get Subscription',
            currentTiers: 'Current tiers: 3% → 5% → 8%',
            withdrawalTitle: 'Withdraw Funds',
            withdrawalSubtitle: 'Withdraw your earnings to your wallet',
            minWithdrawalUSD: 'Minimum withdrawal: $100',
            minWithdrawalRUB: 'Minimum withdrawal: 1000₽',
            processingTime: 'Processing time: 1-7 days',
            withdrawalHistory: 'Withdrawal History',
            noWithdrawals: 'No withdrawals yet',
            setup2FA: 'Setup 2FA',
            setup2FADesc: 'Enable two-factor authentication for secure withdrawals',
            withdrawalNotice: 'Manual Withdrawal Process',
            withdrawalNoticeDesc: 'Currently, referral rewards are processed through our support account. We are working on an automated withdrawal system.',
            contactSupport: 'Contact Support',
            nextRefLevel: 'Referrals required to reach the next level',
        },
        ru: {
            title: 'Пригласить друзей',
            subtitle: 'Получайте награды за каждого друга',
            referralLinkTitle: 'Ваша реферальная ссылка',
            copyButton: 'Копировать',
            copiedButton: 'Скопировано!',
            shareButton: 'Отправить друзьям',
            shareStoryButton: 'Поделиться в сторис',
            totalProfitTitle: 'Общий заработок',
            referralsListTitle: 'Ваши рефералы',
            usd: 'USD',
            rub: 'RUB',
            emptyState: 'Пока нет рефералов',
            emptyStateSubtitle: 'Поделитесь ссылкой, чтобы начать зарабатывать',
            loading: 'Загрузка...',
            modalTitle: 'Детали реферала',
            registeredAt: 'Зарегистрирован',
            converted: 'Конвертирован',
            notConverted: 'Не конвертирован',
            profit: 'Прибыль',
            userPlan: 'План',
            userStatus: 'Статус',
            telegramId: 'Telegram ID',
            languageCode: 'Язык',
            close: 'Закрыть',
            referralProgressTitle: 'Прогресс рефералов',
            referralsCount: 'Всего рефералов',
            currentPercentage: 'Текущая комиссия',
            progressDescription: 'Отслеживайте свой прогресс по реферальной системе и достижением целей для получения большего вознаграждения.',
            milestone5: '0-5 реф.',
            milestone10: '10+ реф.',
            milestone6: '5-9 реф.',
            upgradeTitle: 'Увеличьте свой заработок!',
            upgradeSubtitle: 'Получите подписку для увеличения комиссии с рефералов',
            upgradeDescription: 'С подпиской ваши уровни рефералов будут:',
            upgradeTiers: '5% → 9% → 15%',
            upgradeButton: 'Получить подписку',
            currentTiers: 'Текущие уровни: 3% → 5% → 8%',
            withdrawalTitle: 'Вывод средств',
            withdrawalSubtitle: 'Выводите заработанные средства на свой кошелек',
            minWithdrawalUSD: 'Минимальная сумма вывода: $100',
            minWithdrawalRUB: 'Минимальная сумма вывода: 1000₽',
            processingTime: 'Срок обработки: 1-7 дней',
            withdrawalHistory: 'История выводов',
            noWithdrawals: 'Пока нет выводов',
            setup2FA: 'Настроить 2FA',
            setup2FADesc: 'Включите двухфакторную аутентификацию для безопасных выводов',
            withdrawalNotice: 'Ручной процесс вывода',
            withdrawalNoticeDesc: 'В настоящее время вывод реферальных вознаграждений осуществляется через аккаунт поддержки. Мы работаем над автоматизированной системой выводов.',
            contactSupport: 'Связаться с поддержкой',
            nextRefLevel: 'До следующего уровня нужно:',
            referral: 'рефералов'
        }
    };

    const lang = translations[UserStore.langCode] || translations.en;

    const handleCopyLink = async () => {
        if (!referralLink) return;

        try {
            await navigator.clipboard.writeText(referralLink);
            setShowCopyNotification(true);
            
            // Haptic feedback
            if (TG_WEB_APP?.HapticFeedback) {
                TG_WEB_APP.HapticFeedback.notificationOccurred('success');
            }

            setTimeout(() => setShowCopyNotification(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleShareLink = () => {
        if (!TG_WEB_APP) return;

        try {
            if (shareMsgId || UserStore.shareMsgId) {
                TG_WEB_APP.shareMessage(shareMsgId || UserStore.shareMsgId);
            }
        } catch (err) {
            console.error('Failed to share:', err);
        }
    };

    const handleShareStory = () => {
        if (!referralLink || !TG_WEB_APP) return;

        try {
            TG_WEB_APP.shareMessage({
                message: {
                    text: ` Присоединяйся к DeGuard VPN по моей ссылке и получи бонус!\n\n${referralLink}\n\n#DeGuardVPN #VPN #Безопасность`
                }
            });
        } catch (err) {
            console.error('Failed to share story:', err);
        }
    };

    // Функция для сокращения ссылки
    const truncateLink = (link, maxLength = 30) => {
        if (link.length <= maxLength) return link;
        const start = link.substring(0, 15);
        const end = link.substring(link.length - 10);
        return `${start}...${end}`;
    };

    const handleReferralClick = (referral) => {
        setSelectedReferral(referral);
        setIsModalVisible(true);
        // Блокируем скролл при открытии модального окна
        document.body.style.overflow = 'hidden';
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        // Возвращаем скролл при закрытии модального окна
        document.body.style.overflow = 'scroll';
        setTimeout(() => setSelectedReferral(null), 300);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(UserStore.langCode === 'ru' ? 'ru-RU' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount, currency) => {
        if (!amount) return '0';
        
        const formatter = new Intl.NumberFormat(UserStore.langCode === 'ru' ? 'ru-RU' : 'en-US', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
        
        return formatter.format(amount);
    };

    const getInitials = (firstName, lastName, username) => {
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }
        if (firstName) {
            return firstName.charAt(0).toUpperCase();
        }
        if (username) {
            return username.charAt(0).toUpperCase();
        }
        return '?';
    };

    const getCurrentReferralPercentage = () => {
        return referralData?.profit_percent || 0;
    };

    const getProgressPercentage = () => {
        return progressData?.overall_progress || 0;
    };

    const getActiveTier = () => {
        return progressData?.tiers?.find(tier => tier.is_active) || null;
    };

    const handleUpgradeClick = () => {
        navigate('/vpn');
    };

    const handleContactSupport = () => {
        if (TG_WEB_APP?.openTelegramLink && process.env.REACT_APP_SUPPORT_URL) {
            TG_WEB_APP.openTelegramLink(process.env.REACT_APP_SUPPORT_URL);
        }
    };

    const hasActivePlan = UserStore.userActivePlanData && UserStore.userActivePlanData.length > 0;

    if (isLoading) {
        return (
            <div className={styles.referralsPage}>
                <div className={styles.loadingState}>
                    <div className={styles.loadingSpinner}></div>
                    <p>{lang.loading}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.referralsPage}>
                <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>⚠️</div>
                    <p className={styles.emptyStateText}>Error loading referrals</p>
                </div>
            </div>
        );
    }


    return (
        <div className={styles.referralsPage}>
            {/* Header */}
            <div className={styles.header}>
                <svg className={styles.headerIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 121 80" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd"
                          d="M48.2196 22.986C48.2196 32.5747 40.9859 39.9766 32.799 39.9766C24.6681 39.9766 17.3785 32.5747 17.3785 23.0981C17.3785 13.5654 24.6681 6.5 32.799 6.5C40.9859 6.5 48.2196 13.3971 48.2196 22.986ZM41.159 79.9017H8.68687C2.68689 79.9017 0.5 77.5466 0.5 73.5653C0.5 61.4532 12.9486 46.7056 32.799 46.7056C40.0315 46.7056 46.2796 48.6523 51.3038 51.7122C43.9216 58.1861 39.7528 66.4514 39.7528 73.9019C39.7528 75.9955 40.1618 78.065 41.159 79.9017Z"
                          fill="white"/>
                    <path
                        d="M83.3779 38.9672C92.7984 38.9672 101.097 30.4999 101.097 19.4532C101.097 8.46261 92.7984 0.5 83.3779 0.5C74.0134 0.5 65.7143 8.6869 65.7143 19.5653C65.7143 30.4999 74.0134 38.9672 83.3779 38.9672ZM56.2938 79.9017H110.518C118.032 79.9017 120.5 77.7709 120.5 73.9017C120.5 62.5186 106.256 46.8176 83.3779 46.8176C60.5554 46.8176 46.3125 62.5186 46.3125 73.9017C46.3125 77.7709 48.7797 79.9017 56.2938 79.9017Z"
                        fill="#ff6b35"/>
                </svg>
                <h1 className={styles.title}>{lang.title}</h1>
                <p className={styles.subtitle}>{lang.subtitle}</p>
            </div>

            {/* Referral Link Section */}
            {referralLink && (
                <div className={styles.referralLinkSection}>
                    <h2 className={styles.referralLinkTitle}>{lang.referralLinkTitle}</h2>
                    <div className={styles.referralLinkContainer}>
                        <span className={styles.referralLink} title={referralLink}>
                            {truncateLink(referralLink)}
                        </span>
                        <div className={styles.linkButtons}>
                            <button 
                                className={styles.copyButton}
                                onClick={handleCopyLink}
                            >
                                {showCopyNotification ? (
                                    <Lottie
                                        width={20}
                                        height={20}
                                        options={{
                                            animationData: checkmark,
                                            loop: false,
                                            autoplay: true
                                        }}
                                    />
                                ) : (
                                    lang.copyButton
                                )}
                            </button>
                        </div>
                    </div>
                    <div className={styles.shareButtons}>
                        <button 
                            className={styles.shareButton}
                            onClick={handleShareLink}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                                <polyline points="16,6 12,2 8,6"/>
                                <line x1="12" y1="2" x2="12" y2="15"/>
                            </svg>
                            {lang.shareButton}
                        </button>
                        {TG_WEB_APP.version >= '7.8' && <button
                            className={styles.shareStoryButton}
                            onClick={handleShareStory}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                                <line x1="9" y1="9" x2="9.01" y2="9"/>
                                <line x1="15" y1="9" x2="15.01" y2="9"/>
                            </svg>
                            {lang.shareStoryButton}
                        </button>}
                    </div>
                </div>
            )}

            {/* Referral Progress Section */}
            <div className={styles.referralProgressSection}>
                <div 
                    className={styles.progressHeader}
                    onClick={() => setShowProgressSection(!showProgressSection)}
                >
                    <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between'}}>
                        <h2 className={styles.referralProgressTitle}>{lang.referralProgressTitle}</h2>
                        <div className={styles.expandIcon}>
                            {showProgressSection ? '−' : '+'}
                        </div>
                    </div>
                </div>
                
                {showProgressSection && (
                    <>
                        {/* Upgrade Banner for users without subscription */}
                        {!hasActivePlan && (
                            <div className={styles.upgradeBanner}>
                                <div className={styles.upgradeContent}>
                                    <div className={styles.upgradeIcon}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                        </svg>
                                    </div>
                                    <div className={styles.upgradeText}>
                                        <h3 className={styles.upgradeTitle}>{lang.upgradeTitle}</h3>
                                        <p className={styles.upgradeSubtitle}>{lang.upgradeSubtitle}</p>
                                        <div className={styles.tiersComparison}>
                                            <div className={styles.currentTiers}>
                                                <span className={styles.tiersLabel}>{lang.currentTiers}</span>
                                                <span className={styles.tiersValue}>{lang.currentTiers.split(': ')[1]}</span>
                                            </div>
                                            <div className={styles.upgradeTiers}>
                                                <span className={styles.tiersLabel}>{lang.upgradeDescription}</span>
                                                <span className={styles.tiersValue}>{lang.upgradeTiers}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button className={styles.upgradeButton} onClick={handleUpgradeClick}>
                                        {lang.upgradeButton}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className={styles.progressContainer}>
                            <div className={styles.progressInfo}>
                                <div className={styles.progressStats}>
                                    <div className={styles.progressStat}>
                                        <div className={styles.progressStatValue}>
                                            {referralData?.total_referrals || 0}
                                        </div>
                                        <div className={styles.progressStatLabel}>{lang.referralsCount}</div>
                                    </div>
                                    <div className={styles.progressStat}>
                                        <div className={styles.progressStatValue}>
                                            {getCurrentReferralPercentage()}%
                                        </div>
                                        <div className={styles.progressStatLabel}>{lang.currentPercentage}</div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressTrack}>
                                    <div 
                                        className={styles.progressFill}
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                                <div className={styles.progressMilestones}>
                                    {progressData?.tiers?.map((tier) => (
                                        <div 
                                            key={tier.id} 
                                            className={`${styles.milestone} ${tier.is_active ? styles.active : ''} ${tier.is_completed ? styles.completed : ''}`}
                                        >
                                            <div className={styles.milestoneDot}></div>
                                            <div className={styles.milestoneLabel}>{tier.profit_percent}%</div>
                                            <div className={styles.milestoneSubtext}>{tier.name}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.progressDescription}>
                                <p>{lang.progressDescription}</p>
                                {progressData?.next_tier && progressData.next_tier.referrals_needed > 0 && (
                                    <p className={styles.nextTierInfo}>
                                        {UserStore.langCode === 'ru' ? `${lang.nextRefLevel}: ${progressData.next_tier.referrals_needed} ${lang.referral}` : `${lang.nextRefLevel}:  ${progressData.next_tier.referrals_needed}`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Total Profit Section */}
            {totalProfit && Object.keys(totalProfit).length > 0 && (
                <div className={styles.totalProfitSection}>
                    <div 
                        className={styles.totalProfitHeader}
                        onClick={() => setShowEarningsChart(!showEarningsChart)}
                    >
                        <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between'}}>
                            <h2 className={styles.totalProfitTitle}>{lang.totalProfitTitle}</h2>
                            <div className={styles.expandIcon}>
                            {showEarningsChart ? '−' : '+'}
                            </div>
                        </div>
                        
                        <div>
                            <div className={styles.totalProfitSummary}> 
                                <div className={styles.totalProfitMain}>
                                    ${formatCurrency(totalProfit.total_profit_usd || 0)}
                                </div>
                                <div className={styles.totalProfitMain}>
                                    {formatCurrency(totalProfit.total_profit_rub || 0)}₽                                    
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {showEarningsChart && (
                        <div className={styles.earningsChart}>
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart 
                                    data={[
                                        { date: 'Янв', usd: 0, rub: 0 },
                                        { date: 'Фев', usd: 15, rub: 1200 },
                                        { date: 'Мар', usd: 25, rub: 2000 },
                                        { date: 'Апр', usd: 18, rub: 1500 },
                                        { date: 'Май', usd: 32, rub: 2600 },
                                        { date: 'Июн', usd: 28, rub: 2200 },
                                        { date: 'Июл', usd: 27.5, rub: 880 }
                                    ]}
                                    margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                                >
                                    <defs>
                                        <linearGradient id="usdGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f7931e" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#f7931e" stopOpacity={0.1}/>
                                        </linearGradient>
                                        <linearGradient id="rubGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#ff6b35" stopOpacity={0.1}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }}
                                        axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                                        tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                                    />
                                    <YAxis 
                                        tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 10 }}
                                        axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                                        tickLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                                        tickFormatter={(value) => selectedCurrency === 'usd' ? `$${value}` : `${value}₽`}
                                    />
                                    <Tooltip 
                                        contentStyle={{
                                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '8px',
                                            color: 'white'
                                        }}
                                        formatter={(value, name) => [
                                            selectedCurrency === 'usd' ? `$${value}` : `${value}₽`, 
                                            selectedCurrency === 'usd' ? 'USD Balance' : 'RUB Balance'
                                        ]}
                                        labelFormatter={(label) => `Месяц: ${label}`}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey={selectedCurrency} 
                                        stroke={selectedCurrency === 'usd' ? "#f7931e" : "#ff6b35"} 
                                        strokeWidth={2}
                                        fill={selectedCurrency === 'usd' ? "url(#usdGradient)" : "url(#rubGradient)"}
                                        name={selectedCurrency === 'usd' ? "USD Balance" : "RUB Balance"}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                            
                            <div className={styles.chartDetails}>
                                <div 
                                    className={`${styles.chartDetailItem} ${selectedCurrency === 'usd' ? styles.active : ''}`}
                                    onClick={() => setSelectedCurrency('usd')}
                                >
                                    <div className={styles.chartDetailValue}>${formatCurrency(totalProfit.total_profit_usd || 0)}</div>
                                    <div className={styles.chartDetailLabel}>{lang.usd}</div>
                                </div>
                                <div 
                                    className={`${styles.chartDetailItem} ${selectedCurrency === 'rub' ? styles.active : ''}`}
                                    onClick={() => setSelectedCurrency('rub')}
                                >
                                    <div className={styles.chartDetailValue}>{formatCurrency(totalProfit.total_profit_rub || 0)}₽</div>
                                    <div className={styles.chartDetailLabel}>{lang.rub}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Referrals List Section */}
            <div className={styles.referralsListSection}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                    <h2 className={styles.referralsListTitle}>{lang.referralsListTitle}</h2>
                    <button 
                        onClick={() => refetch()}
                        disabled={isLoading}
                        style={{
                            background: 'rgba(247, 147, 30, 0.2)',
                            border: '1px solid rgba(247, 147, 30, 0.3)',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            color: '#f7931e',
                            fontSize: '12px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: isLoading ? 0.5 : 1
                        }}
                    >
                        {isLoading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>
                
                {referrals && referrals.length > 0 ? (
                    <div className={styles.referralsList}>
                        {referrals.map((referral) => (
                            <div 
                                key={referral.id} 
                                className={styles.referralItem}
                                onClick={() => handleReferralClick(referral)}
                            >
                                <div className={styles.referralAvatar}>
                                    {getInitials(
                                        referral.user_data?.first_name,
                                        referral.user_data?.last_name,
                                        referral.user_data?.username
                                    )}
                                </div>
                                <div className={styles.referralInfo}>
                                    <div className={styles.referralUsername}>
                                        {referral.user_data?.username || referral.user_data?.first_name || 'Unknown'}
                                    </div>
                                    <div className={styles.referralDate}>
                                        {formatDate(referral.registered_at)}
                                    </div>
                                </div>
                                <div className={styles.referralProfit}>
                                    <div className={styles.profitAmount}>
                                        ${formatCurrency(referral.profit_usd)}
                                    </div>
                                    <div className={styles.profitCurrency}>{lang.usd}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>👥</div>
                        <p className={styles.emptyStateText}>{lang.emptyState}</p>
                        <p className={styles.emptyStateText}>{lang.emptyStateSubtitle}</p>
                        {error && (
                            <p className={styles.emptyStateText} style={{color: '#ff6b6b', marginTop: '8px'}}>
                                Error: {error.message || 'Failed to load referrals'}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Withdrawal Section */}
            <div className={styles.withdrawalSection}>
                <div 
                    className={styles.withdrawalHeader}
                    onClick={() => setShowWithdrawalSection(!showWithdrawalSection)}
                >
                    <div style={{display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between'}}>
                        <h2 className={styles.withdrawalTitle}>{lang.withdrawalTitle}</h2>
                        <div className={styles.expandIcon}>
                            {showWithdrawalSection ? '−' : '+'}
                        </div>
                    </div>
                    <p className={styles.withdrawalSubtitle}>{lang.withdrawalSubtitle}</p>
                </div>
                
                {showWithdrawalSection && (
                    <div className={styles.withdrawalContent}>
                        {/* Withdrawal Notice */}
                        <div className={styles.withdrawalNotice}>
                            <div className={styles.noticeIcon}>ℹ️</div>
                            <div className={styles.noticeContent}>
                                <h4 className={styles.noticeTitle}>{lang.withdrawalNotice}</h4>
                                <p className={styles.noticeDescription}>{lang.withdrawalNoticeDesc}</p>
                            </div>
                            <button className={styles.contactSupportButton} onClick={handleContactSupport}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                {lang.contactSupport}
                            </button>
                        </div>

                        {/* Withdrawal Info */}
                        <div className={styles.withdrawalInfo}>
                            <div className={styles.withdrawalInfoItem}>
                                <div className={styles.infoIcon}>💰</div>
                                <div className={styles.infoText}>
                                    <div className={styles.infoTitle}>{lang.minWithdrawalUSD}</div>
                                    <div className={styles.infoTitle}>{lang.minWithdrawalRUB}</div>
                                </div>
                            </div>
                            <div className={styles.withdrawalInfoItem}>
                                <div className={styles.infoIcon}>⏱️</div>
                                <div className={styles.infoText}>
                                    <div className={styles.infoTitle}>{lang.processingTime}</div>
                                </div>
                            </div>
                        </div>



                        {/* Withdrawal History */}
                        <div className={styles.withdrawalHistorySection}>
                            <h3 className={styles.withdrawalHistoryTitle}>{lang.withdrawalHistory}</h3>
                            
                            {/* Empty state */}
                            <div className={styles.withdrawalEmptyState}>
                                <div className={styles.emptyStateIcon}>💳</div>
                                <p className={styles.emptyStateText}>{lang.noWithdrawals}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Модальное окно */}
            {selectedReferral && createPortal(
                <div className={`${styles.modalOverlay} ${isModalVisible ? styles.visible : ''}`}>
                    <div className={styles.modalContent}>
                        <button className={styles.closeButton} onClick={handleModalClose}>
                            ×
                        </button>
                        
                        <div className={styles.modalHeader}>
                            <div className={styles.modalAvatar}>
                                {getInitials(
                                    selectedReferral.user_data?.first_name,
                                    selectedReferral.user_data?.last_name,
                                    selectedReferral.user_data?.username
                                )}
                            </div>
                            <div className={styles.modalUserInfo}>
                                <h3>{selectedReferral.user_data?.username || selectedReferral.user_data?.first_name || 'Unknown'}</h3>
                                <p>@{selectedReferral.user_data?.username || 'no_username'}</p>
                            </div>
                        </div>

                        <div className={styles.modalStats}>
                            <div className={styles.modalStatItem}>
                                <div className={styles.modalStatValue}>
                                    ${formatCurrency(selectedReferral.profit_usd)}
                                </div>
                                <div className={styles.modalStatLabel}>{lang.profit} USD</div>
                            </div>
                            <div className={styles.modalStatItem}>
                                <div className={styles.modalStatValue}>
                                    {formatCurrency(selectedReferral.profit_rub)}₽
                                </div>
                                <div className={styles.modalStatLabel}>{lang.profit} RUB</div>
                            </div>
                        </div>

                        <div className={styles.modalDetails}>
                            <h4>{lang.modalTitle}</h4>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>{lang.registeredAt}</span>
                                <span className={styles.detailValue}>{formatDate(selectedReferral.registered_at)}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>{lang.converted}</span>
                                <span className={styles.detailValue}>
                                    {selectedReferral.converted ? (
                                        <span className={styles.convertedBadge}>{lang.converted}</span>
                                    ) : (
                                        <span className={styles.notConvertedBadge}>{lang.notConverted}</span>
                                    )}
                                </span>
                            </div>
                            {selectedReferral.converted_at && (
                                <div className={styles.detailRow}>
                                    <span className={styles.detailLabel}>Converted At</span>
                                    <span className={styles.detailValue}>{formatDate(selectedReferral.converted_at)}</span>
                                </div>
                            )}
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>{lang.userPlan}</span>
                                <span className={styles.detailValue}>
                                    {selectedReferral.user_data?.user_plan || 'None'}
                                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>{lang.userStatus}</span>
                                <span className={styles.detailValue}>
                                    {selectedReferral.user_data?.user_status ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>{lang.telegramId}</span>
                                <span className={styles.detailValue}>{selectedReferral.user_data?.telegram_id}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>{lang.languageCode}</span>
                                <span className={styles.detailValue}>{selectedReferral.user_data?.language_code?.toUpperCase()}</span>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
});

export { ReferralsPage };